import type { WidgetFieldSource } from "@/domains/buildings/types/buildingWidget";
import { normalizeAttributeValue } from "@/hooks/buildings/useBuildingWidgetData";

/**
 * Sources visible in the hover tooltip for a field showing `currentValue`.
 * User entries always show; EHR / extractor entries only show when their value
 * matches the current value (case- and whitespace-insensitive). Once the user
 * changes a value, supporting files that backed the *previous* value drop out
 * instead of lingering against the new value (WHA-3641).
 *
 * Multi-select needs segment-aware matching: the extractor stores one
 * comma-joined finding (`"Tile,Parquet"`) while EHR stores one row per value
 * (`"tile"`, `"parquet"`). For `isMultiSelect` a source matches when it equals
 * the whole value OR one of its comma-split segments, so per-value EHR support
 * isn't wrongly hidden for a multi-value confirmed field.
 */
export function visibleTooltipSources(
  sources: WidgetFieldSource[],
  currentValue: string,
  isMultiSelect = false
): WidgetFieldSource[] {
  const currentNorm = normalizeAttributeValue(currentValue);
  const segments = isMultiSelect
    ? new Set(
        currentValue
          .split(",")
          .map((s) => normalizeAttributeValue(s))
          .filter(Boolean)
      )
    : null;
  return sources.filter((s) => {
    if (s.kind === "user") return true;
    const v = normalizeAttributeValue(s.value);
    if (v === currentNorm) return true;
    return segments ? segments.has(v) : false;
  });
}
